import React, { useCallback, useEffect, useRef, useState } from "react"
import { DropTargetMonitor, useDrop } from "react-dnd"
import { NativeTypes } from "react-dnd-html5-backend"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useAnalyzeOrderContext } from "@/contexts/AnalyzeOrderContext"
import {
  ANY_FORMAT_ORDERS_FLAG,
  isSupportedOrderFile,
  unsupportedOrderFileMessage,
} from "@/lib/orderFileSupport"

const areFilesValidForAnalyze = (
  files: FileList,
  anyFormatEnabled: boolean,
): boolean => {
  return Array.from(files).every((file) =>
    isSupportedOrderFile(file, anyFormatEnabled),
  )
}

type DropZoneProps = {
  onFileDropped: (files: FileList) => void
}

const DropZone: React.FC<DropZoneProps> = ({ onFileDropped }) => {
  // State for tracking which side is active during drag
  const [dragState, setDragState] = useState<{
    isLeftActive: boolean
    isRightActive: boolean
    isDragging: boolean
  }>({
    isLeftActive: false,
    isRightActive: false,
    isDragging: false,
  })

  const { t } = useTranslation()
  const { handleFileUpload } = useAnalyzeOrderContext()
  const anyFormatEnabled = Boolean(
    useFeatureFlagEnabled(ANY_FORMAT_ORDERS_FLAG),
  )

  // Reference to our own drop container
  const dropContainerRef = useRef<HTMLDivElement | null>(null)

  // Single drop area that covers the entire chat and determines which side was dropped on
  const [{ canDrop, isOver }, dropRef] = useDrop<
    { files: FileList },
    unknown,
    { canDrop: boolean; isOver: boolean }
  >(
    () => ({
      accept: [NativeTypes.FILE],
      hover(item, monitor) {
        // Continuously track mouse position during drag
        const clientOffset = monitor.getClientOffset()
        const dropElement = dropContainerRef.current

        if (clientOffset && dropElement) {
          const rect = dropElement.getBoundingClientRect()
          const relativeX = clientOffset.x - rect.left
          const isLeftSide = relativeX < rect.width / 2

          setDragState((prev) => ({
            ...prev,
            isDragging: true,
            isLeftActive: isLeftSide,
            isRightActive: !isLeftSide,
          }))
        }
      },
      drop({ files }: { files: FileList }, monitor) {
        // Get the drop coordinates relative to the drop target
        const clientOffset = monitor.getClientOffset()
        const dropElement = dropContainerRef.current

        if (clientOffset && dropElement) {
          const rect = dropElement.getBoundingClientRect()
          const relativeX = clientOffset.x - rect.left
          const isLeftSide = relativeX < rect.width / 2

          if (isLeftSide) {
            // Left side - general file upload
            onFileDropped(files)
          } else {
            // Right side - analyze order (only if valid files)
            if (areFilesValidForAnalyze(files, anyFormatEnabled)) {
              handleFileUpload(files, null)
            } else {
              toast.warning(unsupportedOrderFileMessage(anyFormatEnabled))
            }
          }
        } else {
          // Fallback to left side if we can't determine position
          onFileDropped(files)
        }

        // Reset drag state
        setDragState({
          isLeftActive: false,
          isRightActive: false,
          isDragging: false,
        })
      },
      collect(monitor: DropTargetMonitor) {
        // Check if drag originated from within the app
        const isDragFromApp = (window as any).isDraggingFromApp === true
        const canDrop = monitor.canDrop() && !isDragFromApp
        const isOver = monitor.isOver()

        return { canDrop, isOver }
      },
      // Re-create the drop spec when the flag resolves (PostHog flags are async)
    }),
    [anyFormatEnabled, handleFileUpload, onFileDropped],
  )

  // Reset drag state when not over the drop area
  useEffect(() => {
    if (!isOver) {
      setDragState({
        isLeftActive: false,
        isRightActive: false,
        isDragging: false,
      })
    }
  }, [isOver])

  // Combined ref for both drop functionality and position calculations
  const setDropContainer = useCallback(
    (el: HTMLDivElement | null) => {
      dropRef(el)
      dropContainerRef.current = el
    },
    [dropRef],
  )

  // Extract active states for the UI
  const isLeftDropActive = dragState.isDragging && dragState.isLeftActive
  const isRightDropActive = dragState.isDragging && dragState.isRightActive
  const showVisualOverlay =
    dragState.isDragging && (isLeftDropActive || isRightDropActive)

  return (
    <div
      ref={setDropContainer}
      className="absolute inset-0 z-[90]"
      style={{
        pointerEvents: canDrop ? "auto" : "none",
        display: canDrop ? "block" : "none",
      }}
    >
      {/* Visual overlay - only show when dragging */}
      {showVisualOverlay && (
        <div className="absolute inset-0 flex">
          {/* Left drop area - general file upload */}
          <div className="relative h-full w-1/2">
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200/[0.125] backdrop-blur-lg">
              <div className="mx-auto max-w-xs rounded-2xl border-2 border-dashed border-gray-300/30 bg-white/10 px-6 py-8 text-center">
                {/* Upload icon */}
                <div className="mb-4 flex justify-center">
                  <svg
                    className={`h-16 w-16 ${isLeftDropActive ? "text-black dark:text-white" : "text-gray-400"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                {/* Main text */}
                <h2
                  className={`mb-2 text-xl font-bold ${isLeftDropActive ? "text-black dark:text-white" : "text-gray-400"}`}
                >
                  {t("chat.dropZone.upload")}
                </h2>
                {/* Subtitle */}
                <p
                  className={`text-sm ${isLeftDropActive ? "text-black dark:text-gray-200" : "text-gray-500"}`}
                >
                  {t("chat.dropZone.anyFileType")}
                </p>
              </div>
            </div>
          </div>

          {/* Right drop area - analyze order */}
          <div className="relative h-full w-1/2">
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200/[0.125] backdrop-blur-lg">
              <div className="mx-auto max-w-xs rounded-2xl border-2 border-dashed border-gray-300/30 bg-white/10 px-6 py-8 text-center">
                {/* Document icon */}
                <div className="mb-4 flex justify-center">
                  <svg
                    className={`h-16 w-16 ${isRightDropActive ? "text-black dark:text-white" : "text-gray-400"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                {/* Main text */}
                <h2
                  className={`mb-2 text-xl font-bold ${isRightDropActive ? "text-black dark:text-white" : "text-gray-400"}`}
                >
                  {t("chat.dropZone.analyzeOrder")}
                </h2>
                {/* Subtitle */}
                <p
                  className={`text-sm ${isRightDropActive ? "text-black dark:text-gray-200" : "text-gray-500"}`}
                >
                  {anyFormatEnabled
                    ? t("chat.dropZone.anyFormatTypes")
                    : t("chat.dropZone.pdfAndImages")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DropZone
